<template>
  <div class="settings-panel">
    <div class="card">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi bi-gear me-2"></i>Reminder Settings
        </h6>
      </div>
      <div class="card-body">
        <!-- Period Reminder -->
        <div class="form-check mb-3">
          <input
            class="form-check-input"
            type="checkbox"
            :checked="settings.remind_period"
            @change="$emit('update-setting', 'remind_period', $event.target.checked)"
            id="periodReminder"
          />
          <label class="form-check-label" for="periodReminder">
            <strong>Period Start Reminder</strong>
            <p class="text-muted small mb-0">Get notified 3 days before your period starts</p>
          </label>
        </div>

        <!-- Ovulation Reminder -->
        <div class="form-check mb-3">
          <input
            class="form-check-input"
            type="checkbox"
            :checked="settings.remind_ovulation"
            @change="$emit('update-setting', 'remind_ovulation', $event.target.checked)"
            id="ovulationReminder"
          />
          <label class="form-check-label" for="ovulationReminder">
            <strong>Ovulation Reminder</strong>
            <p class="text-muted small mb-0">Get notified when you enter ovulation period</p>
          </label>
        </div>

        <!-- Email Notification -->
        <div class="form-check mb-3">
          <input
            class="form-check-input"
            type="checkbox"
            :checked="settings.email_notification"
            @change="$emit('update-setting', 'email_notification', $event.target.checked)"
            id="emailNotification"
          />
          <label class="form-check-label" for="emailNotification">
            <strong>Email Notifications</strong>
            <p class="text-muted small mb-0">Receive reminders via email</p>
          </label>
        </div>

        <!-- Reminder Days Before -->
        <div class="mb-3">
          <label class="form-label small text-muted">Remind me (days before):</label>
          <select
            class="form-select form-select-sm"
            :value="settings.reminder_days_before || 3"
            @change="$emit('update-setting', 'reminder_days_before', parseInt($event.target.value))"
          >
            <option :value="1">1 day</option>
            <option :value="2">2 days</option>
            <option :value="3">3 days</option>
            <option :value="5">5 days</option>
            <option :value="7">7 days</option>
          </select>
        </div>

        <!-- Save Button -->
        <button class="btn btn-primary btn-sm w-100" @click="$emit('save-settings')">
          <i class="bi bi-check-lg me-1"></i>Save Settings
        </button>
      </div>
    </div>

    <!-- Cycle Statistics Card -->
    <div class="card mt-3">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi bi-graph-up me-2"></i>Cycle Statistics
        </h6>
      </div>
      <div class="card-body">
        <div class="mb-3">
          <small class="text-muted">Average Cycle Length</small>
          <div class="fw-bold fs-5">{{ predictions.averageCycleLength }} days</div>
        </div>
        <div class="mb-3">
          <small class="text-muted">Cycle Regularity</small>
          <div class="fw-bold" :class="getRegularityClass()">
            {{ predictions.isRegular ? 'Regular' : 'Irregular' }}
          </div>
          <small v-if="!predictions.isRegular" class="text-muted">
            Variability: ±{{ predictions.cycleVariability }} days
          </small>
        </div>
        <div>
          <small class="text-muted">Total Records</small>
          <div class="fw-bold">{{ recordsCount }} cycles</div>
        </div>
      </div>
    </div>

    <!-- Legend Card -->
    <div class="card mt-3">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi bi-info-circle me-2"></i>Calendar Legend
        </h6>
      </div>
      <div class="card-body">
        <div class="legend-item mb-2">
          <span class="legend-color period-color"></span>
          <span class="legend-text">Period Days (Actual)</span>
        </div>
        <div class="legend-item mb-2">
          <span class="legend-color predicted-period-color"></span>
          <span class="legend-text">Predicted Period</span>
        </div>
        <div class="legend-item mb-2">
          <span class="legend-color ovulation-color"></span>
          <span class="legend-text">Ovulation Day</span>
        </div>
        <div class="legend-item">
          <span class="legend-color fertile-color"></span>
          <span class="legend-text">Fertile Window</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SettingsPanel',
  props: {
    settings: {
      type: Object,
      default: () => ({
        remind_period: true,
        remind_ovulation: true,
        email_notification: false,
        reminder_days_before: 3
      })
    },
    predictions: {
      type: Object,
      default: () => ({
        averageCycleLength: 28,
        isRegular: false,
        cycleVariability: 0
      })
    },
    recordsCount: {
      type: Number,
      default: 0
    }
  },
  emits: ['update-setting', 'save-settings'],
  methods: {
    getRegularityClass() {
      return this.predictions.isRegular ? 'text-success' : 'text-warning'
    }
  }
}
</script>

<style scoped>
.form-check-label {
  cursor: pointer;
}

.form-check-label strong {
  display: block;
  margin-bottom: 0.25rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  flex-shrink: 0;
}

.period-color {
  background-color: rgba(220, 53, 69, 0.3);
  border: 2px solid #dc3545;
}

.predicted-period-color {
  background-color: rgba(255, 193, 203, 0.3);
  border: 2px solid #ffb3c1;
}

.ovulation-color {
  background-color: rgba(255, 193, 7, 0.3);
  border: 2px solid #ffc107;
}

.fertile-color {
  background-color: rgba(40, 167, 69, 0.2);
  border: 2px solid #28a745;
}

.legend-text {
  font-size: 0.875rem;
}
</style>
